import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const formatStation = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

export default function BumpDipInfoPanel({
  event,
  onClose,
  allEvents,
  onSelectEvent,
  isReversed = false,
}) {
  if (!event) return null;

  const type = (event.event_type || '').toUpperCase();
  const isBump = type === 'BUMP';
  const magnitude = event.magnitude_in || 0;
  const lengthFt = Math.abs((event.to_station_ft || 0) - (event.from_station_ft || 0));
  const displayStartStation = isReversed ? event.to_station_ft : event.from_station_ft;
  const displayEndStation = isReversed ? event.from_station_ft : event.to_station_ft;

  const currentIndex = allEvents?.findIndex(e => e.event_id === event.event_id) ?? -1;
  const totalLen = allEvents?.length ?? 0;
  const hasPrevious = isReversed ? currentIndex < totalLen - 1 : currentIndex > 0;
  const hasNext = isReversed ? currentIndex > 0 : currentIndex >= 0 && currentIndex < totalLen - 1;

  const handlePrevious = () => {
    if (!allEvents) return;
    const idx = isReversed ? currentIndex + 1 : currentIndex - 1;
    if (idx >= 0 && idx < totalLen) onSelectEvent(allEvents[idx]);
  };

  const handleNext = () => {
    if (!allEvents) return;
    const idx = isReversed ? currentIndex - 1 : currentIndex + 1;
    if (idx >= 0 && idx < totalLen) onSelectEvent(allEvents[idx]);
  };

  return (
    <div className="absolute top-3 right-3 bg-white/90 text-gray-800 rounded-lg shadow-lg border border-gray-200 z-[1002] pointer-events-auto backdrop-blur-sm">
      <div className="flex items-center gap-2 px-2 py-1.5">

        {/* Prev/Next */}
        <div className="flex rounded-md overflow-hidden border border-gray-300 shrink-0">
          <button onClick={handlePrevious} disabled={!hasPrevious} className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-30 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <div className="w-px bg-gray-300" />
          <button onClick={handleNext} disabled={!hasNext} className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-30 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Type label */}
        <div className="w-12 flex items-center justify-center shrink-0">
          <span className={`text-xs font-semibold ${isBump ? 'text-red-600' : 'text-orange-600'}`}>
            {isBump ? 'BUMP' : 'DIP'}
          </span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Magnitude */}
        <div className="w-20 flex items-baseline gap-1 shrink-0">
          <span className="text-lg font-bold leading-none text-gray-800">{magnitude.toFixed(2)}</span>
          <span className="text-[10px] text-gray-400 font-medium">in</span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Station Range */}
        <div className="w-52 flex items-center justify-center gap-1 text-xs shrink-0">
          <span className="text-gray-500">Sta</span>
          <span className="font-mono tabular-nums text-gray-700">{formatStation(displayStartStation)}</span>
          <span className="text-gray-400">—</span>
          <span className="font-mono tabular-nums text-gray-700">{formatStation(displayEndStation)}</span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Length & Wheelpath */}
        <div className="w-28 flex items-center gap-2 text-xs shrink-0">
          <span className="font-mono tabular-nums text-gray-500">{lengthFt.toFixed(1)} ft</span>
          <span className="text-gray-600">{event.wheelpath || '—'}</span>
        </div>

        {/* Close */}
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition-colors shrink-0">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
